// components/issues/MyIssuesByUserRtk.tsx
import type { ColDef, GridOptions } from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { ActionIcon, Anchor, Button, CloseButton, Group, Tabs, TextInput } from "@mantine/core";

import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { DisplayDueDate, FailedAlert, MCLoader } from "../../../components";
import { addAppModalThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";

import { GetIssueByUserApiArg, GetIssueByUserApiResponse, useGetIssueByUserQuery } from "../../../../openapi";
import { isValidDateValue } from "../admin/selectors";
import { ISSUE_STATES_OPTIONS } from "./issueConstants";

// ----- typer -----
export type Row = GetIssueByUserApiResponse[number];

type TTab = "assignedItems" | "userOwnedItems" | "itemsWhereUserIsAMember";
const RELATION: Record<TTab, NonNullable<GetIssueByUserApiArg["relation"]>> = {
  assignedItems: "assignedTo",
  userOwnedItems: "owner",
  itemsWhereUserIsAMember: "itemMembers",
};

const StatusCell = ({ value }: { value?: string }) => {
  const option = ISSUE_STATES_OPTIONS.find((status) => status.value === value);

  return (
    <Group gap="xs" wrap="nowrap" h="100%">
      <span className="statusdot" style={{ backgroundColor: option?.color ?? "transparent" }} />
      <span style={{ color: "#58595B", fontSize: 12, whiteSpace: "nowrap" }}>{value?.toUpperCase() ?? "-"}</span>
    </Group>
  );
};

// ----- kolumner -----
const createColumns = (onClickIssue: (row: Row) => void, onClickBoard: (row: Row) => void): ColDef<Row>[] => [
  {
    field: "id",
    headerName: "Id",
    maxWidth: 100,
    editable: false,
    cellRenderer: (p) => (
      <Button color="mc-primary" variant="subtle" px={"xs"} size="sm" onClick={() => onClickIssue(p.data)}>
        <b>
          <u>{p.data.id}</u>
        </b>
      </Button>
      // <Group align="center" gap="xs">
      //   {p.data.id?.length < 5 ? <HSpace /> : null}
      //   <Anchor onClick={() => onClickIssue(p.data)}>{p.data.id}</Anchor>
      // </Group>
    ),
  },
  {
    field: "title",
    headerName: "Tittel",
    minWidth: 200,
    flex: 3,
    editable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    editable: false,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ISSUE_STATES_OPTIONS.map((option) => option.value),
    },
    cellRenderer: (p) => <StatusCell value={p.value} />,
  },
  {
    field: "issueBoard.name",
    headerName: "Register",
    minWidth: 180,
    editable: false,
    cellRenderer: (p) => <Anchor onClick={() => onClickBoard(p.data)}>{p.value}</Anchor>,
  },
  {
    field: "project.maconomy.name",
    headerName: "Prosjekt",
    minWidth: 180,
    editable: false,
    cellRenderer: (p) => <Anchor onClick={() => onClickBoard(p.data)}>{p.value}</Anchor>,
  },
  {
    field: "dates.due",
    headerName: "Frist",
    width: 160,
    editable: false,
    cellRenderer: (p) => {
      const v = p.value as string | undefined;
      if (isValidDateValue(v) === false) return "-";
      return <DisplayDueDate dueDate={v} isComplete={false} />;
    },
  },
];

const MyIssuesByUserRtk: React.FC = () => {
  const projectId = useAppSelector((s) => s.globalReducer.currentProjectId);
  const [activeTab, setActiveTab] = useState<TTab>("assignedItems");
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const gridRef = useRef<AgGridReact<Row>>(null);

  const history = useHistory();
  const dispatch = useAppDispatch();

  const {
    data = [],
    isLoading,
    isFetching,
    error,
  } = useGetIssueByUserQuery(
    {
      relation: RELATION[activeTab],
      projectId,
    },
    { refetchOnMountOrArgChange: true },
  );

  // ⛔️ Behåll data under fetch – nolla inte radlistan
  const rows: Row[] = data ?? [];

  const openIssueModal = useCallback(
    (row: Row) =>
      dispatch(
        addAppModalThunk({
          itemId: row._id,
          boardId: row.issueBoard._id,
          projectId: row.project._id,
          app: AppModalEnums.ISSUE,
        }),
      ),
    [dispatch],
  );

  const goToBoard = useCallback(
    (row: Row) => history.push(`/project/${row.project._id}/issues/${row.issueBoard._id}`),
    [history],
  );

  const columns = useMemo(() => createColumns(openIssueModal, goToBoard), [openIssueModal, goToBoard]);

  // ✅ Memoisera defaultColDef så det inte skapas nytt objekt varje render
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 140,
      suppressHeaderMenuButton: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5", wordBreak: "break-word" },
    }),
    [],
  );

  // ✅ Stabil row-id + immutableData så grid uppdaterar diffat
  const getRowId = useCallback((p: { data: Row }) => p.data._id, []);

  // ✅ Visa gridens overlay i stället för att nolla rader
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (isFetching) api.showLoadingOverlay();
    else api.hideOverlay();
  }, [isFetching]);

  // (valfritt) stabila gridOptions
  const gridOptions = useMemo<GridOptions<Row>>(
    () => ({
      maintainColumnOrder: true,
      suppressColumnMoveAnimation: true,
    }),
    [],
  );

  return (
    <>
      <Group style={{ paddingTop: 8, paddingBottom: 10 }} gap={"xs"}>
        <Tabs
          value={activeTab}
          onChange={(v) => setActiveTab((v as TTab) ?? "assignedItems")}
          styles={{ tab: { fontWeight: 600 } }}
        >
          <Tabs.List>
            <Tabs.Tab style={{ fontSize: ".9em" }} value="assignedItems">
              Ansvarlig
            </Tabs.Tab>
            <Tabs.Tab style={{ fontSize: ".9em" }} value="userOwnedItems">
              Eier
            </Tabs.Tab>
            <Tabs.Tab style={{ fontSize: ".9em" }} value="itemsWhereUserIsAMember">
              Medlem
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
        {showSearch ? (
          <TextInput
            placeholder="Søk…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
            style={{ maxWidth: 280 }}
            autoFocus
            rightSection={
              <CloseButton
                size="sm"
                onClick={() => {
                  setSearch("");
                  setShowSearch(false);
                }}
              />
            }
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowSearch(false);
              }
            }}
          />
        ) : (
          <ActionIcon
            variant="subtle"
            aria-label="Åpne søk"
            size={"sm"}
            color="mc-primary"
            onClick={() => setShowSearch(true)}
          >
            <i className="fa fa-search" />
          </ActionIcon>
        )}
      </Group>

      {isLoading && <MCLoader loading />}
      {!!error && <FailedAlert errorMessage="Kunne ikke laste inn oppgaver" />}

      {!error && (
        <div className="ag-theme-quartz h-100 w-100">
          <AgGridReact<Row>
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            getRowId={getRowId}
            suppressHeaderFocus
            cellSelection
            quickFilterText={search}
            localeText={AG_GRID_LOCALE_NOR}
          />
        </div>
      )}
    </>
  );
};

export default MyIssuesByUserRtk;
